import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CalendarIcon, RefreshCw, Mail, CheckCircle2, XCircle, Ban, AlertTriangle, Send } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type LogRow = {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
  metadata: Record<string, any> | null;
};

type Preset = "24h" | "7d" | "30d" | "custom";
const PAGE_SIZE = 50;


const statusVariant = (status: string) => {
  switch (status) {
    case "sent":
      return "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30";
    case "dlq":
    case "failed":
    case "bounced":
    case "complained":
      return "bg-destructive/15 text-destructive border-destructive/30";
    case "suppressed":
      return "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

const presetToRange = (preset: Preset): { from: Date; to: Date } | null => {
  const now = new Date();
  const to = new Date(now);
  const from = new Date(now);
  if (preset === "24h") from.setDate(from.getDate() - 1);
  else if (preset === "7d") from.setDate(from.getDate() - 7);
  else if (preset === "30d") from.setDate(from.getDate() - 30);
  else return null;
  return { from, to };
};

const AdminEmailDashboard = () => {
  const [preset, setPreset] = useState<Preset>("7d");
  const [customRange, setCustomRange] = useState<{ from?: Date; to?: Date }>({});
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [rows, setRows] = useState<LogRow[]>([]);
  const [templates, setTemplates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [resendTarget, setResendTarget] = useState<LogRow | null>(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);


  const range = useMemo(() => {
    if (preset === "custom") {
      if (customRange.from && customRange.to) {
        return { from: customRange.from, to: customRange.to };
      }
      return null;
    }
    return presetToRange(preset);
  }, [preset, customRange]);

  const fetchData = async () => {
    if (!range) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("email_send_log")
        .select("*")
        .gte("created_at", range.from.toISOString())
        .lte("created_at", range.to.toISOString())
        .order("created_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      setRows((data || []) as LogRow[]);

      const { data: tmpls } = await supabase
        .from("email_send_log")
        .select("template_name")
        .order("template_name")
        .limit(1000);
      const unique = Array.from(new Set((tmpls || []).map((r: any) => r.template_name))).filter(Boolean);
      setTemplates(unique as string[]);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to load email logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customRange.from?.toISOString(), customRange.to?.toISOString()]);

  // Send-time details (template data, resend marker) live on the first
  // `pending` row for a message_id, so collect them per message before the
  // latest-status dedupe throws the earlier rows away.
  const metaByMessage = useMemo(() => {
    const map = new Map<string, Record<string, any>>();
    for (const r of rows) {
      if (!r.message_id || !r.metadata) continue;
      const existing = map.get(r.message_id);
      if (!existing?.template_data && r.metadata.template_data) {
        map.set(r.message_id, r.metadata);
      } else if (!existing) {
        map.set(r.message_id, r.metadata);
      }
    }
    return map;
  }, [rows]);

  const metaFor = (r: LogRow): Record<string, any> | null =>
    (r.message_id ? metaByMessage.get(r.message_id) : null) ?? r.metadata ?? null;

  const deduped = useMemo(() => {
    const map = new Map<string, LogRow>();
    const noKey: LogRow[] = [];
    for (const r of rows) {
      if (!r.message_id) {
        noKey.push(r);
        continue;
      }
      const existing = map.get(r.message_id);
      if (!existing || new Date(r.created_at) > new Date(existing.created_at)) {
        map.set(r.message_id, r);
      }
    }
    return [...Array.from(map.values()), ...noKey].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [rows]);


  const filtered = useMemo(() => {
    return deduped.filter((r) => {
      if (templateFilter !== "all" && r.template_name !== templateFilter) return false;
      if (statusFilter !== "all") {
        if (statusFilter === "failed") {
          if (!["dlq", "failed", "bounced", "complained"].includes(r.status)) return false;
        } else if (r.status !== statusFilter) return false;
      }
      return true;
    });
  }, [deduped, templateFilter, statusFilter]);

  const stats = useMemo(() => {
    const s = { total: filtered.length, sent: 0, failed: 0, suppressed: 0 };
    for (const r of filtered) {
      if (r.status === "sent") s.sent++;
      else if (["dlq", "failed", "bounced", "complained"].includes(r.status)) s.failed++;
      else if (r.status === "suppressed") s.suppressed++;
    }
    return s;
  }, [filtered]);

  // Health alerts — only meaningful with a minimum sample size
  const FAILURE_THRESHOLD = 0.05; // 5%
  const SUPPRESSION_THRESHOLD = 0.10; // 10%
  const MIN_SAMPLE = 10;
  const alerts = useMemo(() => {
    const out: { level: "destructive" | "warning"; title: string; message: string }[] = [];
    if (stats.total < MIN_SAMPLE) return out;
    const failRate = stats.failed / stats.total;
    const suppRate = stats.suppressed / stats.total;
    if (failRate >= FAILURE_THRESHOLD) {
      out.push({
        level: "destructive",
        title: "High failure rate",
        message: `${(failRate * 100).toFixed(1)}% of emails failed (${stats.failed} of ${stats.total}). Threshold: ${(FAILURE_THRESHOLD * 100).toFixed(0)}%.`,
      });
    }
    if (suppRate >= SUPPRESSION_THRESHOLD) {
      out.push({
        level: "warning",
        title: "High suppression rate",
        message: `${(suppRate * 100).toFixed(1)}% of emails were suppressed (${stats.suppressed} of ${stats.total}). Recipients may be unsubscribing or bouncing. Threshold: ${(SUPPRESSION_THRESHOLD * 100).toFixed(0)}%.`,
      });
    }
    return out;
  }, [stats]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const handleResend = async () => {
    if (!resendTarget) return;
    setResending(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-resend-email", {
        body: {
          logId: resendTarget.id,
          recipientEmail:
            resendEmail.trim().toLowerCase() !== resendTarget.recipient_email.toLowerCase()
              ? resendEmail.trim()
              : undefined,
        },
      });
      if (error) {
        let detail = error.message;
        try {
          const ctx = (error as any)?.context;
          if (ctx?.text) {
            const parsed = JSON.parse(await ctx.text());
            detail = parsed.message || parsed.error || detail;
          }
        } catch { /* keep the generic message */ }
        throw new Error(detail);
      }
      toast.success(`Email re-queued to ${(data as any)?.recipient || resendEmail}`);
      setResendTarget(null);
      fetchData();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to resend the email");
    } finally {
      setResending(false);
    }
  };


  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Email Delivery</span>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {(["24h", "7d", "30d"] as Preset[]).map((p) => (
              <Button
                key={p}
                size="sm"
                variant={preset === p ? "default" : "outline"}
                onClick={() => setPreset(p)}
              >
                {p === "24h" ? "Last 24h" : p === "7d" ? "Last 7 days" : "Last 30 days"}
              </Button>
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant={preset === "custom" ? "default" : "outline"}
                  onClick={() => setPreset("custom")}
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {preset === "custom" && customRange.from && customRange.to
                    ? `${format(customRange.from, "MMM d")} – ${format(customRange.to, "MMM d")}`
                    : "Custom range"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: customRange.from, to: customRange.to }}
                  onSelect={(r: any) => setCustomRange({ from: r?.from, to: r?.to })}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Template</label>
              <Select value={templateFilter} onValueChange={(v) => { setTemplateFilter(v); setPage(0); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All templates</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Status</label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="suppressed">Suppressed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {alerts.length > 0 && (
        <div className="space-y-3">
          {alerts.map((a, i) => (
            <Alert
              key={i}
              variant={a.level === "destructive" ? "destructive" : "default"}
              className={a.level === "warning" ? "border-yellow-500/50 text-yellow-900 dark:text-yellow-300 [&>svg]:text-yellow-600" : ""}
            >
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{a.title}</AlertTitle>
              <AlertDescription>{a.message}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total" value={stats.total} icon={<Mail className="h-4 w-4" />} />
        <StatCard label="Sent" value={stats.sent} icon={<CheckCircle2 className="h-4 w-4 text-green-600" />} />
        <StatCard label="Failed" value={stats.failed} icon={<XCircle className="h-4 w-4 text-destructive" />} />
        <StatCard label="Suppressed" value={stats.suppressed} icon={<Ban className="h-4 w-4 text-yellow-600" />} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Email Log {filtered.length > 0 && (
              <span className="text-muted-foreground font-normal text-sm ml-2">
                ({filtered.length} unique emails)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-sm py-8 text-center">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">No emails in this range</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead className="text-right">Resend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r) => {
                    const meta = metaFor(r);
                    const canResend = !!meta?.template_data;
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium text-sm">
                          {r.template_name}
                          {meta?.resent_from && (
                            <Badge variant="outline" className="ml-2 text-[10px]">Resent</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{r.recipient_email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusVariant(r.status)}>
                            {r.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {format(new Date(r.created_at), "MMM d, h:mm a")}
                        </TableCell>
                        <TableCell className="text-xs text-destructive max-w-xs truncate">
                          {r.error_message || ""}
                        </TableCell>
                        <TableCell className="text-right">
                          {canResend ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setResendTarget(r);
                                setResendEmail(r.recipient_email);
                              }}
                            >
                              <Send className="h-3.5 w-3.5 mr-1.5" />
                              Resend
                            </Button>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button size="sm" variant="outline" disabled>
                                      <Send className="h-3.5 w-3.5 mr-1.5" />
                                      Resend
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  This email was sent before resend was available, so its
                                  contents weren't saved. Emails sent from now on can be resent.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!resendTarget} onOpenChange={(o) => !o && setResendTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend this email</DialogTitle>
            <DialogDescription>
              {resendTarget && (
                <>
                  <span className="font-medium">{resendTarget.template_name}</span>, originally
                  sent {format(new Date(resendTarget.created_at), "MMM d, yyyy 'at' h:mm a")}.
                  The recipient gets an identical copy.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="resend-email">Send to</Label>
            <Input
              id="resend-email"
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Change the address if the original was a typo or has bounced.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendTarget(null)} disabled={resending}>
              Cancel
            </Button>
            <Button onClick={handleResend} disabled={resending || !resendEmail.trim()}>
              {resending ? "Sending..." : "Resend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );

};

const StatCard = ({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-muted-foreground">{label}</p>
        {icon}
      </div>
      <p className="text-3xl font-bold">{value}</p>
    </CardContent>
  </Card>
);

export default AdminEmailDashboard;
